import { CheckOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Typography from "style-guide/component/Typography";
import Modal from "style-guide/component/Modal";
import Button from "style-guide/component/Button";

interface CreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreditsModal: React.FC<CreditsModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const features = [
    { title: "Downloadable Justification Reports", icon: "/icons/zigzag.svg" },
    { title: "Advanced Weighted Scoring & Filtering", icon: "/icons/console.svg" },
    { title: "Pricing Analysis vs. Budget", icon: "/icons/lock.svg" },
    {
      title: "Comprehensive Audit Logs for Compliance",
      icon: "/icons/timer.svg",
    },
    {
      title: "Priority Email & Chat Support",
      icon: "/icons/file_check.svg",
    },
  ];

  const plans = [
    {
      credits: "1 Project Credit",
      creditType: "single",
      price: "$1,500",
      badge: null,
    },
    {
      credits: "5 Project Credit",
      creditType: "multiple",
      price: "$6,000",
      badge: {
        text: "Save 20%",
        bg: "bg-[#D8E734]",
        textColor: "text-[#0A4BAA]",
      },
    },
  ];

  const handlePurchase = (plan: typeof plans[0]) => {
    // Navigate to checkout page with plan details
    const planDetails = {
      name: plan.credits,
      price: plan.price,
      type: 'credit',
      creditType: plan.creditType,
      credits: plan.credits
    };
    
    navigate('/checkout', { state: { planDetails } });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Buy Credits"
      size="xl"
      blurBackdrop
      showCloseIcon
      footer={null}
    >
      <div className="flex flex-col items-center gap-6 text-center">
        {/* Content Section */}
        <div 
          className="flex flex-col lg:flex-row w-full rounded-2xl p-6 gap-6"
          style={{
            background: `
              radial-gradient(
                circle at center,
                #090d13 60%,
                #0f428d 80%,
                #487ac4 100%
              )`
          }}
        >
          {/* Left Side - Features */}
          <div className="flex flex-col justify-between gap-6 text-start flex-1">
          <div className="flex flex-col gap-3">
            <Typography
              variant="Heading 2"
              className="text-white !font-medium !text-xl lg:!text-[32px]"
              text="Professional Project Pack"
            />
            <Typography
              variant="Heading 5"
              className="text-white/90"
              text="Get all features from the Professional Plan on a per-project basis."
            />
          </div>
            <div className="flex flex-col gap-4">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-lg p-2 w-8 h-8 flex items-center justify-center">
                    <CheckOutlined className="text-white text-sm" />
                  </div>
                  <Typography
                    variant="Base"
                    className="text-white !font-medium"
                    text={feature.title}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Pricing */}
          <div className="flex flex-col gap-3 flex-shrink-0">
            {plans.map((plan, i) => (
              <div
                key={i}
                className="lg:w-[350px] flex flex-col gap-4 h-full justify-between group bg-white rounded-2xl p-4 border-4 border-white hover:border-blue-primary hover:bg-gray-50 cursor-pointer transition-all duration-200"
              >
                <div className="flex gap-3 items-center">
                  <div className="bg-[#DFE6FE] rounded-lg p-2 w-10">
                    <CheckOutlined />
                  </div>
                  <Typography
                    variant="LG"
                    className="text-[#0A4BAA] !font-medium"
                    text={plan.credits}
                  />
                </div>
                <div className="flex flex-col-reverse items-start lg:items-center lg:flex-row gap-2">
                  <Typography
                    variant="Heading 3"
                    className="text-[#0A4BAA] !font-bold"
                    text={plan.price}
                  />
                  {plan.badge && (
                    <span
                      className={`${plan.badge.bg} ${plan.badge.textColor} text-xs font-medium px-2 py-1 rounded-full`}
                    >
                      {plan.badge.text}
                    </span>
                  )}
                </div>
                <Button
                  type="Primary"
                  buttonText="Purchase"
                  onClick={() => handlePurchase(plan)}
                  className="w-full"
                  size="Medium"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreditsModal;
